import React, { useState } from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { FiAward, FiExternalLink } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const RankBadge = ({ rank }) => {
  const styles = {
    1: 'tw-bg-yellow-400 tw-text-yellow-900',
    2: 'tw-bg-gray-300 tw-text-gray-800',
    3: 'tw-bg-amber-600 tw-text-white',
  };
  return (
    <span className={`tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-xl tw-text-xs tw-font-bold tw-flex-shrink-0 ${styles[rank] || 'tw-bg-gray-100 tw-text-gray-600'}`}>
      {rank}
    </span>
  );
};
RankBadge.propTypes = {
  rank: PropTypes.number.isRequired,
};

const ProjectTable = ({ items, maxValue }) => (
  <div className="tw-space-y-1.5 tw-max-h-[520px] tw-overflow-y-auto tw-pr-1">
    {items.map((item, i) => {
      const pct = maxValue > 0 ? (item.grand_total / maxValue) * 100 : 0;
      return (
        <div key={item.deal_id} className="tw-group hover:tw-bg-gray-50 tw-rounded tw-p-2.5 tw-transition-colors">
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-1">
            <RankBadge rank={i + 1} />
            <div className="tw-flex-1 tw-min-w-0">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-xs tw-font-mono tw-text-gray-400">{item.j_number || item.q_number || '—'}</span>
                <Link
                  to={`/crm/projects/${item.deal_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-truncate hover:tw-underline tw-flex tw-items-center tw-gap-0.5 tw-group-hover:tw-opacity-100"
                >
                  {item.project_name}
                  <FiExternalLink size={10} className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
                </Link>
              </div>
            </div>
            <div className="tw-text-right tw-flex-shrink-0">
              <p className="tw-text-sm tw-font-bold tw-text-gray-900">{fmtM(item.grand_total)}</p>
              <p className="tw-text-xs tw-text-gray-400">
                {fmt(item.contract_count)} contract{item.contract_count !== 1 ? 's' : ''}
                {item.addendum_count > 0 && ` · ${fmt(item.addendum_count)} adm`}
              </p>
            </div>
          </div>
          <div className="tw-ml-8.5 tw-h-1.5 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
            <div
              className="tw-h-full tw-rounded-xl tw-bg-gradient-to-r tw-from-blue-500 tw-to-primary-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          {(item.addendum_value !== 0) && (
            <p className="tw-ml-8.5 tw-text-xs tw-mt-0.5" style={{ color: item.addendum_value < 0 ? '#DC2626' : '#F59E0B' }}>
              {item.addendum_value > 0 ? '+' : ''}{fmtM(item.addendum_value)} addendum
            </p>
          )}
        </div>
      );
    })}
  </div>
);
ProjectTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxValue: PropTypes.number,
};

const CustomerTable = ({ items, maxValue }) => (
  <div className="tw-space-y-1.5 tw-max-h-[520px] tw-overflow-y-auto tw-pr-1">
    {items.map((item, i) => {
      const pct = maxValue > 0 ? (item.grand_total / maxValue) * 100 : 0;
      return (
        <div key={item.customer_id} className="tw-group hover:tw-bg-gray-50 tw-rounded tw-p-2.5 tw-transition-colors">
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-mb-1">
            <RankBadge rank={i + 1} />
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-xs tw-font-semibold tw-text-gray-800 tw-truncate">{item.customer_name || 'Unknown'}</p>
              <p className="tw-text-xs tw-text-gray-400">
                {fmt(item.project_count)} project{item.project_count !== 1 ? 's' : ''}
                {' · '}{fmt(item.contract_count)} contract{item.contract_count !== 1 ? 's' : ''}
                {item.addendum_count > 0 && ` · ${fmt(item.addendum_count)} adm`}
              </p>
            </div>
            <div className="tw-text-right tw-flex-shrink-0">
              <p className="tw-text-sm tw-font-bold tw-text-gray-900">{fmtM(item.grand_total)}</p>
              {(item.addendum_value !== 0) && (
                <p className="tw-text-xs" style={{ color: item.addendum_value < 0 ? '#DC2626' : '#F59E0B' }}>
                  {item.addendum_value > 0 ? '+' : ''}{fmtM(item.addendum_value)}
                </p>
              )}
            </div>
          </div>
          <div className="tw-ml-8.5 tw-h-1.5 tw-bg-gray-100 tw-rounded-xl tw-overflow-hidden">
            <div
              className="tw-h-full tw-rounded-xl tw-bg-gradient-to-r tw-from-emerald-500 tw-to-teal-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      );
    })}
  </div>
);
CustomerTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  maxValue: PropTypes.number,
};

const ContractTopRevenueChart = ({ data, loading, error }) => {
  const [tab, setTab] = useState('project');

  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading top revenue data</p>
    </Card>
  );

  const d = data || {};
  const byProject  = d.by_project  || [];
  const byCustomer = d.by_customer || [];

  const activeItems = tab === 'project' ? byProject : byCustomer;
  const maxValue    = Math.max(...activeItems.map(i => i.grand_total || 0), 1);

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm">
      <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiAward size={16} className="tw-text-yellow-500" />
          <h3 className="tw-font-bold tw-text-gray-900">Top Revenue</h3>
          <span className="tw-text-xs tw-text-gray-400">including addendums</span>
        </div>
        <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
          {[['project', 'By Project'], ['customer', 'By Customer']].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer ${
                tab === key ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="tw-p-5">
        {activeItems.length === 0 ? (
          <p className="tw-text-gray-400 tw-text-center tw-py-8">No data available</p>
        ) : tab === 'project' ? (
          <ProjectTable items={byProject} maxValue={maxValue} />
        ) : (
          <CustomerTable items={byCustomer} maxValue={maxValue} />
        )}
      </div>
    </Card>
  );
};

ContractTopRevenueChart.propTypes = {
  data:    PropTypes.object,
  loading: PropTypes.bool,
  error:   PropTypes.any
};
ContractTopRevenueChart.defaultProps = { data: null, loading: false, error: null };

export default ContractTopRevenueChart;
